// ── Odds conversion ───────────────────────────────────────────────────────────
// Decimal odds → the format the player chose (odds/getOddsType). Used by the
// `odds_type_filter` Vue filter (main.js) and by the parent bridge (oddsDisplay
// in SPORT_SLIP_CHANGED), so the embedding page gets the odds as the player sees them.

function gcd(a, b) {
    a = Math.abs(a);
    b = Math.abs(b);
    while (b) { const t = b; b = a % b; a = t; }
    return a;
}

function formatNumber(num, decimals = 2) {
    return parseFloat(num).toFixed(decimals);
}

function formatAmerican(decimalOdd) {
    const american = decimalOdd >= 2
        ? Math.round((decimalOdd - 1) * 100)
        : Math.round(-100 / (decimalOdd - 1));
    return Intl.NumberFormat('en-US', { signDisplay: 'exceptZero', maximumFractionDigits: 0 })
        .format(american).replace(/,/g, '');
}

function normalizeFraction(numerator, denominator) {
    const divisor = gcd(numerator, denominator);
    return { numerator: numerator / divisor, denominator: denominator / divisor };
}

function toFractionalOdds(decimalOdd, options = {}) {
    if (!decimalOdd || isNaN(decimalOdd) || decimalOdd <= 1) return decimalOdd;

    const config = {
        // Avoid fractions that are "too exact": e.g. no 50/66/80/100
        allowedDenominators: [1, 2, 3, 4, 5, 6, 8, 10, 11, 12, 16, 20, 25, 33],
        ...options
    };

    const fractionalValue = decimalOdd - 1;
    if (Math.abs(fractionalValue - Math.round(fractionalValue)) < 1e-10) {
        const whole = Math.round(fractionalValue);
        return whole === 1 ? '1' : whole + '/1';
    }

    let bestNum = 1, bestDen = 1, bestError = Infinity;
    for (const den of config.allowedDenominators) {
        const num   = Math.round(fractionalValue * den);
        const error = Math.abs(fractionalValue - num / den);
        if (error < bestError || (error === bestError && den < bestDen)) {
            bestError = error; bestNum = num; bestDen = den;
        }
    }

    const { numerator, denominator } = normalizeFraction(bestNum, bestDen);
    return `${numerator}/${denominator}`;
}

export function convertOddsByType(value, type) {
    const d = parseFloat(value);
    if (isNaN(d) || d <= 1) return value;

    switch (type) {
        case 'decimal':                        return formatNumber(d, 2);
        case 'american':                       return formatAmerican(d);
        case 'hongkong': case 'hk':            return formatNumber(d - 1, 2);
        case 'indonesian': case 'indo': case 'in':
            return formatNumber(d >= 2 ? (d - 1) : (-1 / (d - 1)), 2);
        case 'malay': case 'ma':
            return formatNumber(d >= 2 ? (-1 / (d - 1)) : (d - 1), 2);
        case 'uk': case 'fractional':          return toFractionalOdds(d);
        default:                               return formatNumber(d, 2);
    }
}

/**
 * The name of the odds format as the parent bridge documents it: the aliases the skins
 * use (uk, hk, indo, in, ma) are folded, and anything else is shown as decimal.
 *
 * @param {string} type - odds/getOddsType
 * @returns {'decimal'|'american'|'fractional'|'hongkong'|'indonesian'|'malay'}
 */
export function canonicalOddsFormat(type) {
    switch (type) {
        case 'american':                              return 'american';
        case 'uk': case 'fractional':                 return 'fractional';
        case 'hongkong': case 'hk':                   return 'hongkong';
        case 'indonesian': case 'indo': case 'in':    return 'indonesian';
        case 'malay': case 'ma':                      return 'malay';
        default:                                      return 'decimal';
    }
}
